import React, { useState, useEffect, useRef } from 'react';
import styles from './index.module.scss';
import { getImageFromS3 } from '../../../imageService';
import HeadContainer from '../../../components/lm-d-s/head-container';
import { predictions } from './predictions';

const TOTAL_FRAMES = 143;
const ANIMATION_DURATION = 5000;
const PREDICTION_STORAGE_KEY = 'predictionHistory';
const VIEWED_PREDICTIONS_KEY = 'viewedPredictions';

const getTodayDateString = () => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(
    2,
    '0'
  )}-${String(today.getDate()).padStart(2, '0')}`;
};

const getTodayPrediction = () => {
  const stored = localStorage.getItem(PREDICTION_STORAGE_KEY);
  if (!stored) return null;

  try {
    const data = JSON.parse(stored);
    const today = getTodayDateString();

    if (data.date === today) {
      return data.prediction;
    }
    return null;
  } catch (error) {
    console.error('Помилка читання localStorage:', error);
    return null;
  }
};

const saveTodayPrediction = (prediction) => {
  const today = getTodayDateString();
  const data = {
    date: today,
    prediction: prediction,
    timestamp: Date.now()
  };
  localStorage.setItem(PREDICTION_STORAGE_KEY, JSON.stringify(data));
};

const getViewedPredictions = () => {
  const stored = localStorage.getItem(VIEWED_PREDICTIONS_KEY);
  if (!stored) return [];

  try {
    return JSON.parse(stored);
  } catch (error) {
    console.error('Помилка читання переглянутих передбачень:', error);
    return [];
  }
};

const addViewedPrediction = (predictionId) => {
  const viewed = getViewedPredictions();
  if (!viewed.includes(predictionId)) {
    viewed.push(predictionId);
    localStorage.setItem(VIEWED_PREDICTIONS_KEY, JSON.stringify(viewed));
  }
};

const clearViewedPredictions = () => {
  localStorage.setItem(VIEWED_PREDICTIONS_KEY, JSON.stringify([]));
};

const getUnseenPrediction = () => {
  const viewed = getViewedPredictions();
  const unseen = predictions.filter((p) => !viewed.includes(p.id));

  if (unseen.length === 0) {
    clearViewedPredictions();
    return predictions[Math.floor(Math.random() * predictions.length)];
  }

  return unseen[Math.floor(Math.random() * unseen.length)];
};

const PredictionScreen = ({ setScreenNumber, handleBackButton }) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [countdown, setCountdown] = useState(5);
  const [prevCountdown, setPrevCountdown] = useState(null);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [selectedPrediction, setSelectedPrediction] = useState(null);
  const [ballOpened, setBallOpened] = useState(false);
  const [alreadyViewed, setAlreadyViewed] = useState(false);
  const [animationFinished, setAnimationFinished] = useState(false);
  const imagesRef = useRef([]);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const countdownRef = useRef(null);

  const renderFrame = (frameIndex) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');

    if (canvas && ctx && imagesRef.current[frameIndex]) {
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(
        imagesRef.current[frameIndex],
        0,
        0,
        canvas.width,
        canvas.height
      );
    }
  };

  useEffect(() => {
    const todayPrediction = getTodayPrediction();

    if (todayPrediction) {
      setAlreadyViewed(true);
      setSelectedPrediction(todayPrediction);
      return;
    }

    const loadFirstImage = () => {
      const frameNumber = String(0).padStart(5, '0');
      const imagePath = `/images/prediction/Comp 2_${frameNumber}.png`;
      const imageUrl = getImageFromS3(imagePath);

      const img = new Image();
      img.onload = () => {
        imagesRef.current[0] = img;
        renderFrame(0);
        loadRemainingImages();
      };
      img.onerror = (error) => {
        console.error('Помилка завантаження першого кадру:', error);
      };
      img.src = imageUrl;
    };

    const loadRemainingImages = async () => {
      const promises = [];

      for (let i = 1; i < TOTAL_FRAMES; i++) {
        const frameNumber = String(i).padStart(5, '0');
        const imagePath = `/images/prediction/Comp 2_${frameNumber}.png`;
        const imageUrl = getImageFromS3(imagePath);

        const promise = new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
            imagesRef.current[i] = img;
            resolve(img);
          };
          img.onerror = reject;
          img.src = imageUrl;
        });

        promises.push(promise);
      }

      try {
        await Promise.all(promises);
        setImagesLoaded(true);
      } catch (error) {
        console.error('Помилка завантаження кадрів:', error);
      }
    };

    loadFirstImage();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, []);

  const startAnimation = () => {
    if (!imagesLoaded || isAnimating) return;

    setIsAnimating(true);
    setAnimationFinished(false);
    setCurrentFrame(0);
    setCountdown(5);
    setPrevCountdown(null);

    const startTime = Date.now();
    const frameDelay = ANIMATION_DURATION / TOTAL_FRAMES;

    const animateFrames = () => {
      const elapsed = Date.now() - startTime;
      const frameIndex = Math.min(
        Math.floor(elapsed / frameDelay),
        TOTAL_FRAMES - 1
      );

      setCurrentFrame(frameIndex);
      renderFrame(frameIndex);

      if (elapsed < ANIMATION_DURATION) {
        animationRef.current = requestAnimationFrame(animateFrames);
      } else {
        setIsAnimating(false);
        setAnimationFinished(true);
        setCurrentFrame(TOTAL_FRAMES - 1);
        renderFrame(TOTAL_FRAMES - 1);
        setPrevCountdown(null);

        setTimeout(() => {
          const unseenPrediction = getUnseenPrediction();
          setSelectedPrediction(unseenPrediction);

          saveTodayPrediction(unseenPrediction);
          addViewedPrediction(unseenPrediction.id);

          setShowResult(true);

          setTimeout(() => {
            setBallOpened(true);
          }, 2000);
        }, 500);
      }
    };

    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          setPrevCountdown(prev);
          return 0;
        }
        setPrevCountdown(prev);
        return prev - 1;
      });
    }, 1000);

    animationRef.current = requestAnimationFrame(animateFrames);
  };

  if (alreadyViewed && !showResult) {
    return (
      <div
        className={styles.container}
        style={{
          backgroundImage: `url("${getImageFromS3(
            'images/lm-double-splash/violet-background.png'
          )}")`
        }}
      >
        <div style={{ padding: '20px 30px 0' }}>
          <HeadContainer setScreenNumber={setScreenNumber} />
        </div>
        <div className={styles.predictionContent}>
          <div className={styles.alreadyViewedContainer}>
            <div className={styles.alreadyViewedMessage}>
              <h2>Ти вже отримав своє передбачення на сьогодні</h2>
              <p>Повертайся завтра за новим передбаченням!</p>
            </div>
            <img
              src={getImageFromS3('images/prediction/already-viewed.png')}
              alt="Radiance"
              className={styles.alreadyViewedImage}
            />
            <button
              className={styles.menuButton}
              onClick={() => setScreenNumber(1)}
            >
              Далі
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={styles.container}
      style={{
        backgroundImage: `url("${getImageFromS3(
          'images/lm-double-splash/violet-background.png'
        )}")`
      }}
    >
      <div style={{ padding: '20px 30px 0' }}>
        <HeadContainer setScreenNumber={setScreenNumber} />
      </div>
      <div className={styles.predictionContent}>
        {!showResult ? (
          <>
            <div className={styles.title}>
              <h1>Час для передбачення...</h1>
              <p>Магія в кожній кульці</p>
            </div>
            <div className={styles.animationContainer}>
              <div
                className={styles.shadowImage}
                style={{
                  backgroundImage: `url("${getImageFromS3(
                    'images/prediction/circle-shadow.png'
                  )}")`
                }}
              />
              <canvas
                ref={canvasRef}
                className={styles.animationFrame}
                width={800}
                height={800}
              />
            </div>
            <div className={styles.controlContainer}>
              {!isAnimating && !animationFinished ? (
                <button
                  className={styles.shuffleButton}
                  onClick={startAnimation}
                  disabled={!imagesLoaded}
                >
                  Перемішати
                </button>
              ) : isAnimating ? (
                <div className={styles.countdownWrapper}>
                  {prevCountdown !== null && prevCountdown !== countdown && (
                    <div
                      key={`prev-${prevCountdown}`}
                      className={`${styles.countdown} ${styles.countdownExiting}`}
                    >
                      {prevCountdown}
                    </div>
                  )}
                  <div
                    key={`current-${countdown}`}
                    className={`${styles.countdown} ${styles.countdownEntering}`}
                  >
                    {countdown}
                  </div>
                </div>
              ) : null}
            </div>
          </>
        ) : (
          <div className={styles.resultContainer}>
            <div className={styles.radianceContainer}>
              <img
                src={getImageFromS3('images/prediction/radiance.png')}
                alt="Radiance"
                className={styles.radiance}
              />
              <div className={styles.ballContainer}>
                <img
                  src={getImageFromS3('images/prediction/top-ball.png')}
                  alt="Top ball"
                  className={`${styles.topBall} ${
                    ballOpened ? styles.topBallOpen : ''
                  }`}
                />
                <img
                  src={getImageFromS3('images/prediction/bottom-ball.png')}
                  alt="Bottom ball"
                  className={`${styles.bottomBall} ${
                    ballOpened ? styles.bottomBallOpen : ''
                  }`}
                  style={{
                    filter: 'drop-shadow(0 14px 17px rgba(0, 0, 0, 0.25))'
                  }}
                />
                {ballOpened && selectedPrediction && (
                  <div className={styles.predictionText}>
                    {selectedPrediction.text}
                  </div>
                )}
              </div>
            </div>
            <div className={styles.resultActions}>
              <div className={styles.resultMessage}>
                <p>Завтра на тебе чекатиме нове передбачення</p>
              </div>
              <button
                className={styles.menuButton}
                onClick={() => setScreenNumber(1)}
              >
                Далі
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PredictionScreen;
